import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
// ChartHelpButton removed for preset view (no title icon)
import { lightenColor } from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { usePersistWidgetSnapshot } from '../../hooks/usePersistWidgetSnapshot';

/**
 * Semi-circular Gauge Chart Component
 * Size scales with fontSize and container width for responsive behavior
 */
const GaugeChart = ({ percentage, label, color, fontSize = 14, maxSize = 192, isDark = true }) => {
  const clampedPercentage = Math.max(0, Math.min(100, percentage));

  // Use maxSize (passed from parent based on container) but also scale with font
  const scaleFactor = fontSize / 14;
  const size = Math.round(Math.min(maxSize, 192 * scaleFactor));
  const thickness = Math.round(Math.max(8, 16 * (size / 192))); // Scale thickness with size
  const radius = (size - thickness) / 2;
  const circumference = Math.PI * radius;
  const strokeDasharray = `${(clampedPercentage / 100) * circumference} ${circumference}`;

  // Font size for the percentage number scales with the gauge
  const numberFontSize = Math.round(Math.max(14, size * 0.13));

  return (
    <div className="flex flex-col items-center flex-1 min-w-0">
      <div className="relative" style={{ width: size, height: size / 2 + 20 }}>
        <svg width={size} height={size / 2 + thickness} className="overflow-visible">
          {/* Background arc */}
          <path
            d={`M ${thickness / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - thickness / 2} ${size / 2}`}
            fill="none"
            stroke={isDark ? "#3a3c3b" : "#E8EBE9"}
            strokeWidth={thickness}
            strokeLinecap="round"
          />
          {/* Progress arc */}
          <path
            d={`M ${thickness / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - thickness / 2} ${size / 2}`}
            fill="none"
            stroke={color}
            strokeWidth={thickness}
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            style={{ transition: 'stroke-dasharray 0.5s ease, stroke-width 0.3s ease' }}
          />
        </svg>
        {/* Percentage text - positioned in the center of the arc */}
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ top: '20%' }}
        >
          <span className={`${isDark ? 'text-white' : 'text-[#282A29]'} font-bold`} style={{ fontSize: numberFontSize }}>
            {Math.round(clampedPercentage)}%
          </span>
        </div>
      </div>
      <p className="text-[#828382] dark:text-gray-400 mt-2 text-center" style={{ fontSize: Math.max(10, fontSize - 2) }}>{label}</p>
    </div>
  );
};

/**
 * Score Card Component
 */
const ScoreCard = ({ title, value, color, fontSize = 12 }) => {
  return (
    <div className="flex-1 text-center p-2">
      <p
        className="font-semibold mb-0.5"
        style={{ color: lightenColor(color, 0.4), fontSize: fontSize - 2 }}
      >
        {title}
      </p>
      <p className="font-bold" style={{ color, fontSize }}>
        {value}
      </p>
    </div>
  );
};

/**
 * Crop Caster - Season predictions with gauge charts
 */
const PredictionsChart = ({ data = [], commodity = '', forceFiltersOpen = false, presetScorecards = null, presetDescription = '' }) => {
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const [selectedOrigin, setSelectedOrigin] = useState('');
  const [selectedSeason, setSelectedSeason] = useState(1);
  // Preset view only: below lg the two filters sit behind a Filters toggle, as
  // on the other preset charts. The widget dashboard keeps them always open.
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [originModalOpen, setOriginModalOpen] = useState(false);
  const [seasonModalOpen, setSeasonModalOpen] = useState(false);
  const [gaugeMaxSize, setGaugeMaxSize] = useState(192);
  const containerRef = useRef(null);

  const rowDistrict = useCallback((d) => d.district || d.origin || d.region || '', []);

  // Calculate gauge size based on container width
  const calculateGaugeSize = useCallback(() => {
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.offsetWidth;
    // Each gauge gets roughly half the container width minus padding/gaps
    // Container has p-3 (12px each side) and gauges have gap between them
    const availableWidth = (containerWidth - 24 - 24) / 2; // padding + gap / 2 gauges
    const maxSize = Math.max(80, Math.min(250, availableWidth));
    setGaugeMaxSize(maxSize);
  }, []);

  // Observe container resize
  useEffect(() => {
    calculateGaugeSize();
    const resizeObserver = new ResizeObserver(() => {
      calculateGaugeSize();
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [calculateGaugeSize]);

  // Get unique origins and seasons
  const { origins, seasons, selectedData } = useMemo(() => {
    if (!data || data.length === 0) {
      return { origins: [], seasons: [], selectedData: null };
    }

    const uniqueOrigins = [...new Set(data.map(rowDistrict))].filter(Boolean);
    const currentOrigin = selectedOrigin || uniqueOrigins[0] || '';

    // Get seasons for selected district
    const seasonsForDistrict = data
      .filter((d) => rowDistrict(d) === currentOrigin)
      .map((d) => d.season_number);
    const uniqueSeasons = [...new Set(seasonsForDistrict)].sort((a, b) => a - b);

    // Reset season if current doesn't exist
    let currentSeason = selectedSeason;
    if (!uniqueSeasons.includes(selectedSeason)) {
      currentSeason = uniqueSeasons[0] || 1;
    }

    const selected = data.find(
      (d) => rowDistrict(d) === currentOrigin && d.season_number === currentSeason
    );

    return {
      origins: uniqueOrigins,
      seasons: uniqueSeasons,
      selectedData: selected || data[0],
    };
  }, [selectedOrigin, selectedSeason, data, rowDistrict]);

  const hydratePredictions = useCallback(
    (s) => {
      if (!data?.length) return;
      const uniqueOrigins = [...new Set(data.map(rowDistrict))].filter(Boolean);
      let origin = uniqueOrigins[0] || '';
      if (s.selectedOrigin && uniqueOrigins.includes(s.selectedOrigin)) {
        origin = s.selectedOrigin;
      }
      const seasonsForDistrict = data
        .filter((d) => rowDistrict(d) === origin)
        .map((d) => d.season_number);
      const uniqueSeasons = [...new Set(seasonsForDistrict)].sort((a, b) => a - b);
      let season = uniqueSeasons[0] ?? 1;
      if (s.selectedSeason != null && uniqueSeasons.includes(s.selectedSeason)) {
        season = s.selectedSeason;
      }
      setSelectedOrigin(origin);
      setSelectedSeason(season);
    },
    [data, rowDistrict]
  );

  const snapshotPredictions = useCallback(
    () => ({ selectedOrigin, selectedSeason }),
    [selectedOrigin, selectedSeason]
  );

  usePersistWidgetSnapshot(
    'predictions',
    commodity,
    !!(data && data.length > 0),
    hydratePredictions,
    snapshotPredictions,
    [selectedOrigin, selectedSeason]
  );

  // After persistence hydrate (same layout phase, declared below the hook), keep origin valid
  // without overwriting batched setState from onHydrate — the old useEffect saw stale '' and
  // called setSelectedOrigin(origins[0]), racing hydration and wiping Firestore-backed selection.
  useLayoutEffect(() => {
    if (!origins.length) return;
    setSelectedOrigin((prev) => {
      if (prev && origins.includes(prev)) return prev;
      return origins[0];
    });
  }, [origins]);

  // Update season if adjusted
  useEffect(() => {
    if (seasons.length > 0 && !seasons.includes(selectedSeason)) {
      setSelectedSeason(seasons[0]);
    }
  }, [seasons, selectedSeason]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return t('charts.cropCaster.none');
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return t('charts.cropCaster.none');
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return t('charts.cropCaster.none');
    }
  };

  const helpDescription = "AI-powered price and volume predictions showing forecasted trends. Gauge charts display confidence levels for price direction and volume expectations for each origin.";

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex flex-col bg-transparent">
      <div className="px-4">
        <div className="border-b py-2 flex-shrink-0" style={{ borderColor: theme.borderColor }}>
          <div className="flex items-center gap-1">
            <h3 className="text-[#5D625F] uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4 }}>
              {t('charts.cropCaster.title')}
            </h3>
          </div>
        </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-[#828382] dark:text-gray-400 text-center" style={{ fontSize }}>
            {t('charts.cropCaster.noData')}<br />{t('charts.selectAnotherTimeframe')}
          </p>
        </div>
      </div>
    );
  }

  if (!selectedData) {
    return (
      <div className="h-full flex flex-col bg-transparent">
      <div className="px-4">
        <div className="border-b py-2 flex-shrink-0" style={{ borderColor: theme.borderColor }}>
          <div className="flex items-center gap-1">
            <h3 className="text-[#5D625F] uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4 }}>
              {t('charts.cropCaster.title')}
            </h3>
          </div>
        </div>
      </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-[#828382] dark:text-gray-400 text-center" style={{ fontSize }}>
            {t('charts.cropCaster.noDataFilters')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-transparent">
      <div className="flex-1 flex flex-col min-h-0 px-4 gap-3 overflow-y-auto">
      {/* Title */}
      <div className="px-4">
      <div className="border-b py-2 flex-shrink-0" style={{ borderColor: theme.borderColor }}>
        <div className="flex items-center gap-1">
          <h3 className="text-[#5D625F] uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4 }}>
            {t('charts.cropCaster.title')}
          </h3>
        </div>
      </div>
      </div>

      {/* Filters / Cards / Chart */}
        {/* Filters */}
        <div className="flex-shrink-0">
          {forceFiltersOpen ? (
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="w-full flex items-center justify-between py-2 border-b flex-shrink-0 lg:hidden"
              style={{ borderColor: theme.borderSecondary }}
            >
              <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('filters.filters')}</span>
              {filtersExpanded ? <ChevronUp size={16} style={{ color: theme.secondaryText }} /> : <ChevronDown size={16} style={{ color: theme.secondaryText }} />}
            </button>
          ) : null}

          <div
            className={
              (forceFiltersOpen
                ? 'pt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 lg:flex lg:flex-nowrap lg:overflow-x-auto lg:scrollbar-hide'
                : 'pt-2 grid grid-cols-2 gap-2') +
              (forceFiltersOpen ? (filtersExpanded ? ' max-lg:pt-3' : ' max-lg:hidden') : '')
            }
          >
            <FilterDropdown
              label={t('filters.origin')}
              value={selectedOrigin || t('filters.selectOrigin')}
              onClick={() => setOriginModalOpen(true)}
            />
            <FilterDropdown
              label={t('filters.season')}
              value={`${t('filters.season')} ${selectedSeason}`}
              onClick={() => setSeasonModalOpen(true)}
            />
          </div>
        </div>

        {/* Cards */}
        {presetScorecards ? <div className="flex-shrink-0">{presetScorecards}</div> : null}

        {/* Chart */}
        <div ref={containerRef} className="bg-white dark:bg-[#191919] rounded-lg overflow-hidden">
          {presetDescription ? (
            <div className="px-4 pt-4 pb-3">
              {/* Filter summary only — the graph name lives in the card title above. */}
              <p className="text-xs text-[#828382] dark:text-gray-400 text-center">
                {(() => {
                  const commodityLabel = commodity
                    ? commodity
                        .split(' ')
                        .filter(Boolean)
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ')
                    : 'this commodity';

                  const parts = [];
                  parts.push(selectedOrigin || t('filters.selectOrigin'));
                  parts.push(`${t('filters.season')} ${selectedSeason}`);
                  const summary = parts.filter(Boolean).join(' · ');
                  return summary ? `${commodityLabel}, ${summary}` : commodityLabel;
                })()}
              </p>
            </div>
          ) : null}
          <div className="p-3">
          {/* Row 1: Expected vs Actual Start */}
          <div className="flex mb-2">
            <ScoreCard
              title={t('charts.cropCaster.expectedStart')}
              value={formatDate(selectedData.expected_start_date)}
              color="#4CAF50"
            />
            <ScoreCard
              title={t('charts.cropCaster.actualStart')}
              value={formatDate(selectedData.season_start)}
              color="#FF9800"
            />
          </div>

          {/* Row 2: Expected vs Actual End */}
          <div className="flex mb-2">
            <ScoreCard
              title={t('charts.cropCaster.expectedEnd')}
              value={formatDate(selectedData.expected_finish_date)}
              color="#4CAF50"
            />
            <ScoreCard
              title={t('charts.cropCaster.actualEnd')}
              value={formatDate(selectedData.season_end)}
              color="#FF9800"
            />
          </div>

          {/* Row 3: Expected vs Actual Volume */}
          <div className="flex mb-3">
            <ScoreCard
              title={t('charts.cropCaster.expectedVol')}
              value={`${selectedData.expected_volume?.toLocaleString() || '0'} lbs`}
              color="#4CAF50"
            />
            <ScoreCard
              title={t('charts.cropCaster.actualVol')}
              value={`${selectedData.vol_to_date?.toLocaleString() || '0'} lbs`}
              color="#FF9800"
            />
          </div>

          {/* Gauge Charts */}
          <div className="flex justify-around pt-2 gap-4">
            <GaugeChart
              percentage={selectedData.perc_season_length || 0}
              label={t('charts.cropCaster.timeProgress')}
              color="#4CAF50"
              maxSize={gaugeMaxSize}
              fontSize={fontSize}
              isDark={isDark}
            />
            <GaugeChart
              percentage={selectedData.perc_vol || 0}
              label={t('charts.cropCaster.volumeProgress')}
              color="#FF9800"
              maxSize={gaugeMaxSize}
              fontSize={fontSize}
              isDark={isDark}
            />
          </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <FilterModal
        isOpen={originModalOpen}
        onClose={() => setOriginModalOpen(false)}
        title={t('filters.selectOrigin')}
        options={origins}
        selectedValues={selectedOrigin}
        onSelect={(value) => {
          setSelectedOrigin(value);
        }}
      />

      <FilterModal
        isOpen={seasonModalOpen}
        onClose={() => setSeasonModalOpen(false)}
        title={t('filters.selectSeason')}
        options={seasons.map((s) => `${t('filters.season')} ${s}`)}
        selectedValues={`${t('filters.season')} ${selectedSeason}`}
        onSelect={(value) => {
          const seasonNum = parseInt(value.replace(`${t('filters.season')} `, ''));
          setSelectedSeason(seasonNum);
        }}
      />
    </div>
  );
};

export default PredictionsChart;

